"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FormRadioGroup } from "@/components/form/form-radio-group";
import { Form } from "@/components/ui/form";
import { sortingData } from "../data/sorting";

const _formSchema = z.object({
  sortOption: z.string().optional(),
});

type DietsListSortFormProps = {
  children?: React.ReactNode;
  onSubmitted?: () => void;
  submitOnChange?: boolean;
};

// Only for RWD purposes
export const DietsListSortForm = ({
  children,
  onSubmitted,
  submitOnChange = false,
}: DietsListSortFormProps) => {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();

  const form = useForm<z.infer<typeof _formSchema>>({
    defaultValues: {
      sortOption: searchParams.get("sortOption") || "",
    },
  });

  const submitSortOption = useCallback(
    (sortOption?: string) => {
      const nextParams = new URLSearchParams(searchParams);
      if (sortOption) {
        nextParams.set("sortOption", sortOption);
      } else {
        nextParams.delete("sortOption");
      }
      nextParams.set("page", "1");
      onSubmitted?.();
      replace(`${pathname}?${nextParams.toString()}`);
    },
    [onSubmitted, pathname, replace, searchParams],
  );

  const onSubmit = (data: z.infer<typeof _formSchema>) => {
    if (data.sortOption) {
      submitSortOption(data.sortOption);
    } else {
      submitSortOption();
    }
  };

  const watchedSortOption = form.watch("sortOption");

  useEffect(() => {
    if (!submitOnChange) {
      return;
    }

    const currentSortOption = searchParams.get("sortOption") || "";
    if ((watchedSortOption || "") === currentSortOption) {
      return;
    }

    submitSortOption(watchedSortOption);
  }, [searchParams, submitOnChange, submitSortOption, watchedSortOption]);

  return (
    <Form {...form}>
      <form
        className="flex flex-1 grow flex-col"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormRadioGroup
          fieldName="sortOption"
          form={form}
          radioGroupClassName="gap-4"
          options={sortingData.map((item) => ({
            label: t(`diets.filters.sort.${item.label}`),
            value: item.value === "default" ? "" : item.value,
          }))}
        />

        {children}
      </form>
    </Form>
  );
};
